import { Injectable } from '@angular/core';
import {
  GraphBuilder,
  GraphComponent,
  GraphInputMode,
  GraphItemTypes,
  INode,
  Rect,
  ViewportLimitingPolicy,
} from '@yfiles/yfiles';
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import {
  BusinessProcessGraphBuildResult,
  ProcessConnection,
  ProcessNodeTag,
} from '../business-process/models/business-process.model';
import {
  createBusinessModel,
  toProcessNodeDefinition,
} from '../business-process/models/process-node-mapping';
import { BusinessProcessNodeStyle } from '../business-process/styles/business-process-node-style';
import { BusinessProcessNodeSizingService } from '../business-process/services/business-process-node-sizing.service';
import { BusinessProcessLabelService } from '../business-process/services/business-process-label.service';

@Injectable()
export class BuildModelService {
  constructor(
    private processSizing: BusinessProcessNodeSizingService,
    private processLabels: BusinessProcessLabelService,
  ) {}

  // builds the nodes of the chosen models' processes, and returns the connections between them for the business
  // process pipeline to draw
  public buildAnalysisModel(
    models: { _key: string; name: string; colour: string }[],
    graphData: {
      nodes: AnalysisNode[];
      edges?: AnalysisEdge[];
      connections?: AnalysisEdge[];
    },
    graphComponent: GraphComponent,
  ): BusinessProcessGraphBuildResult {
    const graph = graphComponent.graph;
    graph.clear();

    // how each node is drawn: its process type, name and its model's colours
    const processModels = new Map(
      (models ?? []).map((m) => [m._key, createBusinessModel(m._key, m.name, m.colour)]),
    );
    const connectionData = graphData?.connections ?? graphData?.edges ?? [];

    // only the chosen models, and one node for each key
    const nodes = [
      ...new Map(
        (graphData?.nodes ?? [])
          .filter((n) => processModels.has(n.modelKey))
          .map((n) => [n._key, n]),
      ).values(),
    ];
    const edges = connectionData.filter((e) => processModels.has(e.modelKey));

    const processNodes = new Map(
      nodes.map((n) => [n._key, toProcessNodeDefinition(n, processModels.get(n.modelKey)!)]),
    );

    const graphBuilder = new GraphBuilder(graph);
    const nodesSource = graphBuilder.createNodesSource(
      nodes,
      (nodeDataItem) => nodeDataItem._key,
    );

    // edges aren't built here: the business process pipeline creates them from the returned connections,
    // so they can be attached to ordered ports

    // the style only reads the tag, so one instance serves every node
    nodesSource.nodeCreator.defaults.style = new BusinessProcessNodeStyle();

    // each node is sized to fit its name
    nodesSource.nodeCreator.layoutProvider = (data: AnalysisNode) => {
      const process = processNodes.get(data._key)!;
      const size = this.processSizing.getSize(process.type, process.label);
      return new Rect(0, 0, size.width, size.height);
    };

    // each graph node's tag holds its data under `node`, which the analysis page, scorecard and
    // business process pipeline all read, and how it is drawn under `process`
    nodesSource.nodeCreator.tagProvider = (data: AnalysisNode): ProcessNodeTag => {
      return {
        node: data,
        process: processNodes.get(data._key)!,
      };
    };

    graphBuilder.buildGraph();

    for (const node of graph.nodes) this.processLabels.addNodeLabel(graph, node);

    // the process can be panned and zoomed but not selected
    const im = graphComponent.inputMode as GraphInputMode;
    if (im) {
      im.enabled = true;
      im.selectableItems = GraphItemTypes.NONE;
      im.moveViewportInputMode.enabled = true;
    }
    graphComponent.updateContentBounds();
    graphComponent.viewportLimiter.policy = ViewportLimitingPolicy.UNRESTRICTED;
    graphComponent.fitContent();

    const nodesByKey = new Map<string, INode>();
    for (const node of graph.nodes) {
      const data = (node.tag as ProcessNodeTag | undefined)?.node;
      if (data?._key) nodesByKey.set(data._key, node);
    }

    const connections: ProcessConnection[] = edges
      .map((edge): ProcessConnection | null => {
        const source = nodesByKey.get(edge.from);
        const target = nodesByKey.get(edge.to);
        if (!source || !target) return null;
        return {
          source,
          target,
          edge,
          label: edge.informationName,
          definition: {
            sourceId: edge.from,
            targetId: edge.to,
            label: edge.informationName,
          },
        };
      })
      .filter((edge): edge is ProcessConnection => edge !== null);

    return {
      nodes: nodesByKey,
      connections,
    };
  }
}
